import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IeltsMiniRequest } from '../../Client/request';
import { readStoredUserData } from '../../authentication/userSession';
import { miniAnswers, miniDraftState } from './miniAnswers';

export const isMiniDraft = status => ['draft', 'in_progress'].includes(status);
export const isMiniPending = status => ['pending', 'pending_evaluation', 'processing', 'evaluating', 'queued'].includes(status);

export default function useMiniAttempt({ initialAttempt, detail, onExit }) {
  const [attempt, setAttempt] = useState(initialAttempt);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [conflict, setConflict] = useState(false);
  const [saveStatus, setSaveStatus] = useState('Saved');
  const [editVersion, setEditVersion] = useState(0);
  const module = detail.exercise.module;
  const user = readStoredUserData();
  const key = `ielts-mini:${user?.UserId || user?.id || 'current'}:${initialAttempt.id}`;
  const revision = useRef(initialAttempt.revision);
  const queue = useRef(Promise.resolve());
  const submitting = useRef(false);
  const mounted = useRef(true);
  const [initialDraft] = useState(() => {
    let cached;
    try { cached = JSON.parse(localStorage.getItem(key)); } catch (_) { /* Storage may be unavailable. */ }
    const hasCache = isMiniDraft(initialAttempt.status) && cached?.state;
    const usableCache = hasCache
      && (initialAttempt.revision == null || cached.revision === initialAttempt.revision);
    const differs = hasCache && !usableCache
      && JSON.stringify(miniAnswers(module, cached.state, detail.sections)) !== JSON.stringify(initialAttempt.answers || {});
    return {
      state: usableCache ? cached.state : miniDraftState(module, initialAttempt.answers, detail.sections),
      recovery: differs ? cached.state : null,
    };
  });
  const [initialState, setInitialState] = useState(initialDraft.state);
  const [recovery, setRecovery] = useState(initialDraft.recovery);
  const recoveryRef = useRef(initialDraft.recovery);
  const latestState = useRef(initialState);
  const latestAnswers = useRef(miniAnswers(module, initialState, detail.sections));
  const lastSaved = useRef(JSON.stringify(initialAttempt.answers || {}));

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const remember = useCallback(() => {
    if (recoveryRef.current) return;
    try {
      localStorage.setItem(key, JSON.stringify({ revision: revision.current, state: latestState.current }));
    } catch (_) {
      if (mounted.current) setSaveStatus('Browser backup unavailable');
    }
  }, [key]);

  const onChange = useCallback(state => {
    if (submitting.current || recoveryRef.current) return;
    latestState.current = state;
    const answers = miniAnswers(module, state, detail.sections);
    const changed = JSON.stringify(answers) !== JSON.stringify(latestAnswers.current);
    latestAnswers.current = answers;
    remember();
    if (changed && mounted.current) {
      setSaveStatus('Unsaved changes');
      setEditVersion(version => version + 1);
    }
  }, [detail.sections, module, remember]);

  // Serialize writes and read the latest input when each write starts. A slow
  // response updates only the revision, never the student's current answers.
  const save = useCallback(() => {
    if (recoveryRef.current) return Promise.reject(new Error('Choose which draft to keep before saving.'));
    const next = queue.current.catch(() => {}).then(async () => {
      const answers = latestAnswers.current;
      const serialized = JSON.stringify(answers);
      const data = await IeltsMiniRequest(`/attempts/${encodeURIComponent(initialAttempt.id)}`, 'PATCH', {
        answers,
        ...(revision.current != null ? { revision: revision.current } : {}),
      });
      if (data.revision != null) revision.current = data.revision;
      lastSaved.current = serialized;
      remember();
      if (mounted.current) {
        setSaveStatus(serialized === JSON.stringify(latestAnswers.current) ? 'Saved' : 'Unsaved changes');
        setError('');
      }
      return data;
    });
    queue.current = next;
    next.catch(err => { if (err.status === 409 && mounted.current) setConflict(true); });
    return next;
  }, [initialAttempt.id, remember]);

  useEffect(() => {
    if (!isMiniDraft(attempt.status) || busy || recovery) return;
    if (lastSaved.current === JSON.stringify(latestAnswers.current)) return;
    const timer = setTimeout(() => {
      save().catch(err => {
        if (mounted.current) { setError(err.message); setSaveStatus('Save failed'); }
      });
    }, 5000);
    return () => clearTimeout(timer);
  }, [attempt.status, busy, recovery, editVersion, save]);

  const onSubmit = useCallback(async answers => {
    if (submitting.current || recoveryRef.current || !isMiniDraft(attempt.status)) return;
    submitting.current = true;
    setBusy(true);
    setError('');
    if (answers) {
      latestAnswers.current = answers;
      latestState.current = miniDraftState(module, answers, detail.sections);
      remember();
    }
    let submitStarted = false;
    try {
      await save();
      submitStarted = true;
      const result = await IeltsMiniRequest(`/attempts/${encodeURIComponent(initialAttempt.id)}/submit`, 'POST', {
        answers: latestAnswers.current,
      });
      if (mounted.current) setAttempt({ ...initialAttempt, ...result, answers: result.answers || latestAnswers.current });
      try { localStorage.removeItem(key); } catch (_) { /* Submission is already saved. */ }
      return result;
    } catch (err) {
      if (submitStarted) {
        try {
          const recovered = await IeltsMiniRequest(`/attempts/${encodeURIComponent(initialAttempt.id)}`);
          if (!isMiniDraft(recovered.status)) {
            if (mounted.current) setAttempt(recovered);
            try { localStorage.removeItem(key); } catch (_) { /* Server has the attempt. */ }
            return recovered;
          }
        } catch (_) { /* Keep the local backup when the connection is unavailable. */ }
      }
      if (mounted.current) setError(err.message || 'Submission failed. Your answers are preserved.');
      throw err;
    } finally {
      submitting.current = false;
      if (mounted.current) setBusy(false);
    }
  }, [attempt.status, detail.sections, initialAttempt, key, module, remember, save]);

  const onSave = useCallback(async () => {
    if (submitting.current || recoveryRef.current) return;
    await save();
    onExit();
  }, [onExit, save]);

  const refresh = useCallback(async () => {
    const data = await IeltsMiniRequest(`/attempts/${encodeURIComponent(initialAttempt.id)}`);
    if (mounted.current) setAttempt(data);
    return data;
  }, [initialAttempt.id]);

  useEffect(() => {
    if (!isMiniPending(attempt.status)) return;
    let cancelled = false;
    let timer;
    let polls = 0;
    const poll = async () => {
      if (cancelled || polls >= 20) return;
      if (document.visibilityState === 'hidden') { timer = setTimeout(poll, 15000); return; }
      polls += 1;
      try {
        const data = await refresh();
        if (!isMiniPending(data.status)) return;
      } catch (err) {
        if (!cancelled && mounted.current) setError(err.message);
      }
      if (!cancelled) timer = setTimeout(poll, 15000);
    };
    timer = setTimeout(poll, 15000);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [attempt.status, refresh]);

  const retry = async () => {
    if (submitting.current) return;
    submitting.current = true;
    setBusy(true);
    setError('');
    try {
      const result = await IeltsMiniRequest(`/attempts/${encodeURIComponent(initialAttempt.id)}/retry`, 'POST');
      if (mounted.current) setAttempt(previous => ({ ...previous, ...result }));
    } catch (err) { if (mounted.current) setError(err.message); }
    finally { submitting.current = false; if (mounted.current) setBusy(false); }
  };

  const saveLocalAnswers = async () => {
    if (submitting.current || recoveryRef.current) return;
    submitting.current = true;
    setBusy(true);
    try {
      const current = await IeltsMiniRequest(`/attempts/${encodeURIComponent(initialAttempt.id)}`);
      if (!isMiniDraft(current.status)) { setAttempt(current); return; }
      revision.current = current.revision;
      await save();
      if (mounted.current) setConflict(false);
    } catch (err) { if (mounted.current) setError(err.message); }
    finally { submitting.current = false; if (mounted.current) setBusy(false); }
  };

  const resolveRecovery = async choice => {
    if (submitting.current || !recoveryRef.current || !['browser', 'server'].includes(choice)) return;
    const browserState = recoveryRef.current;
    submitting.current = true;
    setBusy(true);
    setError('');
    try {
      const current = await IeltsMiniRequest(`/attempts/${encodeURIComponent(initialAttempt.id)}`);
      if (!isMiniDraft(current.status)) {
        if (choice === 'browser') throw new Error('This attempt is already submitted. Your browser backup is retained.');
        setAttempt(current);
        recoveryRef.current = null;
        setRecovery(null);
        try { localStorage.removeItem(key); } catch (_) { /* The server attempt is already saved. */ }
        return;
      }
      revision.current = current.revision;
      const state = choice === 'browser' ? browserState : miniDraftState(module, current.answers, detail.sections);
      latestState.current = state;
      latestAnswers.current = miniAnswers(module, state, detail.sections);
      recoveryRef.current = null;
      if (choice === 'browser') await save();
      else {
        lastSaved.current = JSON.stringify(current.answers || {});
        remember();
      }
      setInitialState(state);
      setAttempt(current);
      setRecovery(null);
      setConflict(false);
      setSaveStatus('Saved');
    } catch (err) {
      recoveryRef.current = browserState;
      if (mounted.current) setError(err.message);
    } finally {
      submitting.current = false;
      if (mounted.current) setBusy(false);
    }
  };

  const session = useMemo(() => ({ initialState, onChange, onSubmit, onSave, partIndex: detail.partIndex }),
    [initialState, onChange, onSubmit, onSave, detail.partIndex]);
  return { attempt, busy, error, conflict, recovery, resolveRecovery, saveLocalAnswers, saveStatus, refresh, retry, session };
}
